import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RUPEE } from "../../constants/globalConstants";
import { appApiHandler } from "../../api/appApiHandler";
import {
  getErrorMessage,
  getImageUrl,
  getInitials,
  getOptimizedUrl,
  getPrice,
} from "../../constants/globalFunction";
import { setWhishlist } from "../../store/slices/whishlistState";
import { setCartCount, setWhishlistCount } from "../../store/slices/countState";
import {
  FaHeart,
  FaRegHeart,
  FaDownload,
  FaFileArchive,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaPlay,
} from "react-icons/fa";
import {
  LuCopy,
  LuSend
} from "react-icons/lu";
import JSZip from "jszip";
import ImageViewer from "../../components/ImageViewer";
import CommonButton from "../../components/button/CommonButton";
import CartDrawer from "../../components/cart/CartDrawer";
import ShareProduct from "../../components/modal/ShareProduct";
import { AuthModal } from "../../components/modal";
import BuyNowModal from "./components/buyNowModal";
import "./ProductDetails.css";
import { colors } from "../../constants/colors";
import { showErrorToast } from "../../utils/toast";
import { string } from "../../constants/string";
import CommonFooter from "../../components/footer/CommonFooter";
import HomeHeader from "../../components/header/HomeHeader";
import Breadcrumb from "../../components/breadcrumb";
import ProductCard from "../home/components/ProductCard";
import { getUserType } from "../../constants/asyncStoreManager";
import RatingSection from "./components/RatingSection";

const ProductDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userData } = useSelector((state) => state.userState);
  const { whishlist } = useSelector((state) => state.whishlistState);

  // Get product_id from URL params or navigation state
  const urlParams = new URLSearchParams(window.location.search);
  const urlProductId = urlParams.get("product_id");
  const {
    product_id: stateProductId,
    cartSelectedSize,
    cartSelectedColor,
    fromPage,
  } = location?.state || {};

  const product_id = urlProductId || stateProductId;

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [productPrice, setProductPrice] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [isImageViewVisible, setIsImageViewVisible] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [cartProducts, setCartProducts] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [sortedImages, setSortedImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [preloadedImageUrls, setPreloadedImageUrls] = useState({});
  const [preloadedVideoUrl, setPreloadedVideoUrl] = useState(null);
  const [preloadedVideoThumbnailUrl, setPreloadedVideoThumbnailUrl] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [returnPolicyExpanded, setReturnPolicyExpanded] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [activeCouponIndex, setActiveCouponIndex] = useState(0);
  const [copiedCouponId, setCopiedCouponId] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isAuthModalVisible, setAuthModalVisible] = useState(false);
  const [isBuyNowModalOpen, setBuyNowModalOpen] = useState(false);
  const [isSizeChartExpanded, setIsSizeChartExpanded] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);

  const scrollViewRef = useRef(null);
  const isManualScrollRef = useRef(false);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const isWishlisted = whishlist?.some(
    (wishItem) => wishItem?.product_id === product?._id
  );

  // Function to determine breadcrumb items based on source page
  const getBreadcrumbItems = () => {
    const baseItems = [
      {
        label: "Home",
        path: "/",
      },
    ];

    // If we have explicit fromPage in state, use it
    if (fromPage) {
      switch (fromPage) {
        case "home":
          return [
            ...baseItems,
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "wishlist":
          return [
            ...baseItems,
            {
              label: "My Wishlist",
              path: "/whishlist",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "cart":
          return [
            ...baseItems,
            {
              label: "Cart",
              path: "/cart",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "category":
          return [
            ...baseItems,
            {
              label: "Category",
              path: "/category",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "search":
          return [
            ...baseItems,
            {
              label: "Search",
              path: "/search",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "notification":
          return [
            ...baseItems,
            {
              label: "Notifications",
              path: "/notification",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        default:
          return [
            ...baseItems,
            {
              label: "Product Details",
              isActive: true,
            },
          ];
      }
    }

    // Try to detect source from document.referrer (for new tab navigation)
    const referrer = document.referrer;
    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        const pathname = referrerUrl.pathname;
        const hostname = referrerUrl.hostname;

        // Only process if it's from the same domain
        if (hostname === window.location.hostname || hostname === "localhost") {
          if (pathname === "/" || pathname === "") {
            return [
              ...baseItems,
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/whishlist")) {
            return [
              ...baseItems,
              {
                label: "My Wishlist",
                path: "/whishlist",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/cart")) {
            return [
              ...baseItems,
              {
                label: "Cart",
                path: "/cart",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/category")) {
            return [
              ...baseItems,
              {
                label: "Category",
                path: "/category",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/search")) {
            return [
              ...baseItems,
              {
                label: "Search",
                path: "/search",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/notification")) {
            return [
              ...baseItems,
              {
                label: "Notifications",
                path: "/notification",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          }
        }
      } catch (error) {
        console.log("Error parsing referrer URL:", error);
      }
    }

    // Default breadcrumb
    return [
      ...baseItems,
      {
        label: "Product Details",
        isActive: true,
      },
    ];
  };

  const getWishlistProducts = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getWishlist(reqData);
    if (response?.status === 200 && response?.data) {
      dispatch(setWhishlist(response?.data));
      dispatch(setWhishlistCount(response?.data?.length));
    }
  };

  const getCartProducts = async () => {
    if (!userData) return;
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getCart(reqData);
    setCartLoading(false);
    if (response?.status === 200 && response?.data) {
      dispatch(setCartCount(response?.data?.products?.length));
      setCartProducts(response?.data?.products);
    }
  };

  const parseCouponDate = (dateString) => {
    if (!dateString || typeof dateString !== "string") return null;
    const [day, month, year] = dateString.split("-");
    if (!day || !month || !year) return null;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsedDate?.getTime()) ? null : parsedDate;
  };

  const isCouponValidForDisplay = (coupon) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiryDate = parseCouponDate(coupon?.expiry_date);
    if (!coupon?.expiry_date) {
      return true;
    }

    if (!expiryDate) return false;

    return expiryDate >= today;
  };

  const getAllCoupons = async () => {
    const response = await appApiHandler.getAllCoupons();
    if (response?.status === 200 && Array.isArray(response?.data)) {
      const validCoupons = response.data.filter(isCouponValidForDisplay);
      setCoupons(validCoupons);
      setActiveCouponIndex(0);
    }
  };

  const getCouponTitle = (coupon) => {
    if (!coupon) return "";
    const minOrderAmount = parseFloat(coupon?.min_order_amount || 0);
    if (coupon?.discount_type === "percentage") {
      if (minOrderAmount > 0) {
        return `${coupon?.discount_percentage || 0}% off on orders above ${RUPEE}${minOrderAmount}`;
      }
      return `${coupon?.discount_percentage || 0}% off`;
    }
    if (minOrderAmount > 0) {
      return `Flat ${RUPEE}${coupon?.discount_amount || 0} off on orders above ${RUPEE}${minOrderAmount}`;
    }
    return `Flat ${RUPEE}${coupon?.discount_amount || 0} off`;
  };

  const handleCopyCouponCode = async (coupon) => {
    if (!coupon?.code) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopiedCouponId(coupon?._id);
      setTimeout(() => setCopiedCouponId(null), 1800);
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = coupon.code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedCouponId(coupon?._id);
      setTimeout(() => setCopiedCouponId(null), 1800);
    }
  };

  const onPressWishlist = async () => {
    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }
    const reqData = {
      user_id: userData?._id,
      product_id: product?._id,
    };
    const response = isWishlisted
      ? await appApiHandler.removeFromWishlist(reqData)
      : await appApiHandler.addToWishlist(reqData);
    if (response?.status === 200) {
      getWishlistProducts();
    } else if (response?.message) {
      showErrorToast(getErrorMessage(response?.message));
    } else {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const getProductDetails = async () => {
    const reqData = {
      product_id: product_id,
      user_id: userData?._id,
    };
    const response = await appApiHandler.getProductDetails(reqData);
    if (response?.status === 200) {
      const productData = response?.data;
      setProduct(productData);
      if (productData?.colors && productData?.images) {
        const images = [];

        productData?.colors.forEach((color) => {
          const colorImages = productData?.images.filter(
            (img) => img?.color?._id === color?._id
          );
          images.push(...colorImages);
        });

        const remainingImages = productData?.images.filter(
          (img) =>
            !img?.color ||
            !productData?.colors.some((color) => color?._id === img?.color?._id)
        );
        images.push(...remainingImages);

        setSortedImages(images);
      } else {
        setSortedImages(productData?.images || []);
      }
    }
    setLoading(false);
  };

  const getRelatedProducts = async (categoryId) => {
    if (!categoryId) return;
    
    const userType = (await getUserType()) || "retailer";
    const reqData = {
      category_id: categoryId,
      user_type: userType,
    };
    const response = await appApiHandler.filterProducts(reqData);
    if (response?.status === 200 && response?.data) {
      const filteredProducts = response?.data?.filter(
        (item) => item?._id !== product_id
      );
      setRelatedProducts(filteredProducts || []);
    }
  };

  useEffect(() => {
    setLoading(true);
    getProductDetails();
    getCartProducts();
    getWishlistProducts();
    getAllCoupons();
  }, []);

  useEffect(() => {
    if (activeCouponIndex >= coupons.length) {
      setActiveCouponIndex(0);
    }
  }, [coupons, activeCouponIndex]);

  // Set breadcrumb items when component mounts
  useEffect(() => {
    setBreadcrumbItems(getBreadcrumbItems());
  }, [fromPage]);

  // Set initial active tab based on available content
  useEffect(() => {
    if (product) {
      if (product?.description && !product?.return_policy) {
        setDescriptionExpanded(true);
        setReturnPolicyExpanded(false);
      } else if (product?.return_policy && !product?.description) {
        setReturnPolicyExpanded(true);
        setDescriptionExpanded(false);
      } else if (product?.description && product?.return_policy) {
        // If both are available, default to description
        setDescriptionExpanded(true);
        setReturnPolicyExpanded(false);
        console.log("both are available-=-=-=-=-",product?.category_id);
      }
      
      // Fetch related products when product data is loaded
      if (product?.category_id) {
        getRelatedProducts(product?.category_id);
      }
    }
  }, [product]);

  useEffect(() => {
    const getProductPrice = async () => {
      if (selectedSize) {
        const price = await getPrice(selectedSize);
        setProductPrice(price);
      }
    };

    if (product) {
      if (!selectedSize) {
        let sizeToSelect = null;

        if (cartSelectedSize) {
          const cartSize = product?.sizes?.find(
            (size) => size?._id === cartSelectedSize
          );

          if (cartSize && isProductInStock(cartSize?.unit)) {
            sizeToSelect = cartSize;
          }
        }

        if (!sizeToSelect) {
          sizeToSelect = product?.sizes?.find((size) =>
            isProductInStock(size?.unit)
          );
        }

        setSelectedSize(sizeToSelect);
      }

      if (!selectedColor) {
        let colorToSelect = null;

        if (cartSelectedColor) {
          colorToSelect = product?.colors?.find(
            (color) => color?._id === cartSelectedColor
          );
        }

        if (colorToSelect) {
          handleColorSelection(colorToSelect);
        } else {
          // const allColorOption = {
          //   _id: "all-color",
          //   name: "All",
          //   hex_code: "#000000",
          // };
          // setSelectedColor(allColorOption);
          // Set first color that has an image as selected color
          const firstColorWithImage = sortedImages?.find(img => img?.color)?.color;
          if (firstColorWithImage) {
            handleColorSelection(firstColorWithImage);
          } else {
            setSelectedColor({});
          }
        }
      }

      getProductPrice();
    }
  }, [product, selectedSize]);

  const isProductInStock = (unit) => {
    return unit && unit > 0;
  };

  const handleColorSelection = (color) => {
    if (!sortedImages || !color) {
      return;
    }

    if (color?._id === "all-color") {
      setSelectedColor(color);
      return;
    }

    const imageIndex = sortedImages.findIndex(
      (img) => img?.color?._id === color?._id
    );
    if (imageIndex !== -1 && scrollViewRef.current) {
      isManualScrollRef.current = true;
      scrollViewRef.current.scrollTo({
        left: imageIndex * scrollViewRef.current.offsetWidth,
        behavior: "smooth",
      });
      setActiveIndex(imageIndex);
      setTimeout(() => {
        isManualScrollRef.current = false;
      }, 500);
    }

    setSelectedColor(color);
  };

  const openCartDrawer = () => {
    setIsCartDrawerOpen(true);
  };

  const closeCartDrawer = () => {
    setIsCartDrawerOpen(false);
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate percentage position for square magnifier
    // Adjust for magnifier size to center it properly
    const magnifierSize = 120; // Should match CSS magnifier width/height
    const xPercent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const yPercent = Math.max(0, Math.min(100, (y / rect.height) * 100));

    setZoomPosition({ x: xPercent, y: yPercent });
  };

  const handleMouseEnter = () => {
    setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    setIsZoomed(false);
  };

  // Preload zoom view for instant response
  const preloadZoomView = useMemo(() => {
    if (!filteredImages[activeIndex]?.image) return null;
    return getImageUrl(filteredImages[activeIndex]?.image);
  }, [filteredImages, activeIndex]);

  // Helper function to get preloaded image URL or fallback
  const getPreloadedImageUrl = (imagePath, compressionQuality = 0.95) => {
    if (!imagePath) return "";
    return preloadedImageUrls[imagePath] || getOptimizedUrl(imagePath, compressionQuality);
  };

  const handleAddToCart = async () => {
    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }
    if (!selectedColor || selectedColor?._id === "all-color") {
      showErrorToast("Please select a specific color");
      return;
    }
    const reqData = {
      user_id: userData?._id,
      product_id: product?._id,
      size_id: selectedSize?._id,
      color_id: selectedColor?._id,
      qty: 1,
    };
    setCartLoading(true);
    const response = await appApiHandler.addToCart(reqData);

    if (response?.status === 200) {
      getCartProducts();
    } else if (response?.message) {
      setCartLoading(false);
      showErrorToast(getErrorMessage(response?.message));
    } else {
      setCartLoading(false);
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const hasInStockSize = useMemo(
    () => product?.sizes?.some((size) => isProductInStock(size?.unit)),
    [product?.sizes]
  );

  const isProductInCart = useMemo(() => {
    if (!product || !selectedSize) return false;
    return cartProducts.some((item) => {
      const sameProduct = item?._id === product?._id;
      const sameSize = item?.size_id === selectedSize?._id;
      if (product?.colors?.length > 0) {
        return sameProduct && sameSize && item?.color_id === selectedColor?._id;
      } else {
        return sameProduct && sameSize;
      }
    });
  }, [cartProducts, product, selectedSize, selectedColor]);

  const handleDownloadImagesSingle = async () => {
    try {
      if (!sortedImages?.length) return;

      // Get product name for organized filenames
      const rawName = product?.name || "product";
      const productName = (
        rawName.length > 20 ? rawName.substring(0, 20) : rawName
      ).replace(/[^a-zA-Z0-9]/g, "_");

      // Organize images by color for better naming
      const imagesByColor = {};
      const imagesWithoutColor = [];

      sortedImages.forEach((img) => {
        if (img?.color) {
          const colorName = img?.color?.name || "Color";
          if (!imagesByColor[colorName]) {
            imagesByColor[colorName] = [];
          }
          imagesByColor[colorName].push(img);
        } else {
          imagesWithoutColor.push(img);
        }
      });

      let downloadCount = 0;

      // Download images organized by color
      for (const [colorName, images] of Object.entries(imagesByColor)) {
        const colorFolderName = colorName
          .substring(0, 20)
          .replace(/[^a-zA-Z0-9]/g, "_");

        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          if (!img?.image) continue;

          const imageUrl = getImageUrl(img?.image);

          try {
            const response = await fetch(imageUrl, { mode: "cors" });
            if (!response.ok) throw new Error("Network response was not ok");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            // Create organized filename
            const originalFileName = img?.image?.split("/").pop() || "";
            const extension = originalFileName.includes(".")
              ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
              : "jpg";

            const fileName = `${productName}_${colorFolderName}_${i + 1
              }.${extension}`;

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
            downloadCount++;
          } catch (err) {
            console.error(
              `❌ Failed to download ${colorName} image ${i + 1}:`,
              err
            );
          }

          // Delay between downloads
          if (downloadCount < sortedImages.length) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }

      // Download images without color
      for (let i = 0; i < imagesWithoutColor.length; i++) {
        const img = imagesWithoutColor[i];
        if (!img?.image) continue;

        const imageUrl = getImageUrl(img?.image);

        try {
          const response = await fetch(imageUrl, { mode: "cors" });
          if (!response.ok) throw new Error("Network response was not ok");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          // Create organized filename
          const originalFileName = img?.image?.split("/").pop() || "";
          const extension = originalFileName.includes(".")
            ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
            : "jpg";

          const fileName = `${productName}_General_${i + 1}.${extension}`;

          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          window.URL.revokeObjectURL(url);
          downloadCount++;
        } catch (err) {
          console.error(`❌ Failed to download general image ${i + 1}:`, err);
        }

        // Delay between downloads
        if (downloadCount < sortedImages.length) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      console.error("Error in download function:", error);
    }
  };

  const handleDownloadImagesAsZip = async () => {
    try {
      if (!sortedImages?.length) return;
      setDownloading(true);
      setDownloadProgress(0);

      const rawName = product?.name || "product";
      const productName = (
        rawName.length > 20 ? rawName.substring(0, 20) : rawName
      ).replace(/[^a-zA-Z0-9]/g, "_");

      const zip = new JSZip();
      const productFolder = zip.folder(productName);

      const imagesByColor = {};
      const imagesWithoutColor = [];

      sortedImages.forEach((img) => {
        if (img?.color) {
          const colorName = img?.color?.name || "Color";
          if (!imagesByColor[colorName]) {
            imagesByColor[colorName] = [];
          }
          imagesByColor[colorName].push(img);
        } else {
          imagesWithoutColor.push(img);
        }
      });

      let totalImages = 0;
      Object.values(imagesByColor).forEach(
        (arr) => (totalImages += arr.length)
      );
      totalImages += imagesWithoutColor.length;
      let downloadedCount = 0;

      for (const [colorName, images] of Object.entries(imagesByColor)) {
        const colorFolderName = colorName
          .substring(0, 20)
          .replace(/[^a-zA-Z0-9]/g, "_");
        const colorFolder = productFolder.folder(colorFolderName);

        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          if (!img?.image) {
            continue;
          }

          const imageUrl = getImageUrl(img?.image);

          try {
            let response;
            try {
              response = await fetch(imageUrl, {
                mode: "cors",
                headers: {
                  Accept: "image/*",
                },
              });
            } catch (fetchError) {
              response = await fetch(imageUrl);
            }

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            const originalFileName = img?.image?.split("/").pop() || "";
            const extension = originalFileName.includes(".")
              ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
              : "jpg";

            const fileName = `${colorFolderName}_${i + 1
              }_${Date.now()}.${extension}`;

            colorFolder.file(fileName, blob);

            downloadedCount++;
            setDownloadProgress(
              Math.round((downloadedCount / totalImages) * 100)
            );
          } catch (err) {
            console.error(
              `❌ Failed to process ${colorName} image ${i + 1}:`,
              err
            );

            // Fallback: try to create a placeholder file
            const fileName = `${colorFolderName}_${i + 1}_error.txt`;
            colorFolder.file(
              fileName,
              `Failed to download: ${imageUrl}\nError: ${err.message}`
            );
          }
        }
      }

      // Process images without color
      for (let i = 0; i < imagesWithoutColor.length; i++) {
        const img = imagesWithoutColor[i];
        if (!img?.image) {
          continue;
        }

        const imageUrl = getImageUrl(img?.image);

        try {
          // Try different fetch approaches
          let response;
          try {
            response = await fetch(imageUrl, {
              mode: "cors",
              headers: {
                Accept: "image/*",
              },
            });
          } catch (fetchError) {
            console.log("CORS fetch failed, trying without CORS mode");
            response = await fetch(imageUrl);
          }

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();

          // Create organized filename
          const originalFileName = img?.image?.split("/").pop() || "";
          const extension = originalFileName.includes(".")
            ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
            : "jpg";

          const fileName = `${productName}_${i + 1}_${Date.now()}.${extension}`;

          productFolder.file(fileName, blob);

          downloadedCount++;
          setDownloadProgress(
            Math.round((downloadedCount / totalImages) * 100)
          );
        } catch (err) {
          console.error(`❌ Failed to process general image ${i + 1}:`, err);

          // Fallback: try to create a placeholder file
          const fileName = `${productName}_${i + 1}_error.txt`;
          productFolder.file(
            fileName,
            `Failed to download: ${imageUrl}\nError: ${err.message}`
          );
        }
      }

      // Generate and download ZIP file

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = zipUrl;
      link.download = `${productName}_${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(zipUrl);

      setDownloading(false);
      setDownloadProgress(0);
    } catch (error) {
      console.error("Error downloading images as zip:", error);
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Preload all images when sortedImages is set
  useEffect(() => {
    if (!sortedImages || sortedImages.length === 0) return;

    const preloadImages = async () => {
      setPreloadedImageUrls((prev) => {
        const preloadedUrls = { ...prev };
        const imagesToPreload = [];

        // Collect images that need to be preloaded
        for (const img of sortedImages) {
          if (!img?.image) continue;

          const imagePath = img.image;
          // Check if already preloaded in state
          if (!preloadedUrls[imagePath]) {
            imagesToPreload.push(imagePath);
          }
        }

        // Preload images in parallel (non-blocking)
        imagesToPreload.forEach((imagePath) => {
          const optimizedUrl = getOptimizedUrl(imagePath);
          const imgElement = new Image();

          imgElement.onload = () => {
            setPreloadedImageUrls((current) => ({
              ...current,
              [imagePath]: optimizedUrl,
            }));
          };
          imgElement.onerror = () => {
            // Fallback to non-optimized URL
            const fallbackUrl = getImageUrl(imagePath);
            const fallbackImg = new Image();
            fallbackImg.onload = () => {
              setPreloadedImageUrls((current) => ({
                ...current,
                [imagePath]: fallbackUrl,
              }));
            };
            fallbackImg.onerror = () => {
              // Use optimized URL anyway
              setPreloadedImageUrls((current) => ({
                ...current,
                [imagePath]: optimizedUrl,
              }));
            };
            fallbackImg.src = fallbackUrl;
          };
          imgElement.src = optimizedUrl;
        });

        return preloadedUrls;
      });
    };

    preloadImages();
  }, [sortedImages]);

  // Preload video when product is set
  useEffect(() => {
    if (!product?.video) {
      setPreloadedVideoUrl(null);
      setPreloadedVideoThumbnailUrl(null);
      return;
    }

    // Preload video URL
    if (product.video?.video) {
      const videoUrl = getImageUrl(product.video.video);
      const video = document.createElement('video');
      video.preload = 'auto';
      video.src = videoUrl;
      video.oncanplaythrough = () => {
        setPreloadedVideoUrl(videoUrl);
      };
      video.onerror = () => {
        setPreloadedVideoUrl(videoUrl); // Use URL anyway
      };
    }

    // Preload video thumbnail image
    if (product.video?.image) {
      const thumbnailUrl = getOptimizedUrl(product.video.image);
      const imgElement = new Image();
      imgElement.onload = () => {
        setPreloadedVideoThumbnailUrl(thumbnailUrl);
      };
      imgElement.onerror = () => {
        // Fallback to non-optimized URL
        const fallbackUrl = getImageUrl(product.video.image);
        const fallbackImg = new Image();
        fallbackImg.onload = () => {
          setPreloadedVideoThumbnailUrl(fallbackUrl);
        };
        fallbackImg.onerror = () => {
          setPreloadedVideoThumbnailUrl(thumbnailUrl); // Use optimized URL anyway
        };
        fallbackImg.src = fallbackUrl;
      };
      imgElement.src = thumbnailUrl;
    }
  }, [product?.video]);

  useEffect(() => {
    if (!selectedColor) {
      setFilteredImages(sortedImages);
      return;
    }

    if (selectedColor?._id === "all-color") {
      setFilteredImages(sortedImages);
      return;
    }

    const colorImages = sortedImages.filter(
      (img) => img?.color?._id == selectedColor?._id
    );
    if (colorImages.length > 0) {
      setFilteredImages(colorImages);
    } else {
      setFilteredImages(sortedImages);
    }
  }, [selectedColor, sortedImages]);

  useEffect(() => {
    // Always default to first image (0), not video
    // Video will only show when its thumbnail is clicked
    setActiveIndex(0);
    setIsVideoPlaying(false);
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  }, [filteredImages]);

  const displayColors = useMemo(() => {
    if (!product?.colors || !sortedImages) return product?.colors || [];

    const filteredColors = product.colors.filter((color) => {
      if (
        color?.name === "No Color" &&
        color?.hex_code === "rgba(0, 0, 0, 0)"
      ) {
        return sortedImages.some((img) => img?.color?._id === color?._id);
      }
      return true;
    });

    const colorIdsWithImages = [];
    sortedImages.forEach((img) => {
      if (img?.color?._id && !colorIdsWithImages.includes(img?.color?._id)) {
        colorIdsWithImages.push(img?.color?._id);
      }
    });

    const colorsWithImages = colorIdsWithImages
      .map((id) => filteredColors.find((color) => color?._id === id))
      .filter(Boolean);

    const colorsWithoutImages = filteredColors.filter(
      (color) => !colorIdsWithImages.includes(color?._id)
    );

    const allColorOption = {
      _id: "all-color",
      name: "All",
      hex_code: "#000000",
    };

    // Find "No Color" option
    const noColorOption = [...colorsWithImages, ...colorsWithoutImages].find(
      (color) =>
        color?.name === "No Color" && color?.hex_code === "rgba(0, 0, 0, 0)"
    );

    // Get other colors (excluding "No Color")
    const otherColors = [...colorsWithImages, ...colorsWithoutImages].filter(
      (color) =>
        !(color?.name === "No Color" && color?.hex_code === "rgba(0, 0, 0, 0)")
    );

    // Return in the desired order: All Color, No Color, Other Colors
    const orderedColors = [];
    // const orderedColors = [allColorOption];
    if (noColorOption) {
      orderedColors.push(noColorOption);
    }
    orderedColors.push(...otherColors);

    return orderedColors;
  }, [product, sortedImages]);

  const activeCoupon =
    coupons?.length > 0 ? coupons[activeCouponIndex] : null;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isImageViewVisible) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          if (activeIndex === -1) {
            setActiveIndex(filteredImages.length - 1);
            setIsVideoPlaying(false);
          } else if (activeIndex === 0 && product?.video) {
            setActiveIndex(-1);
            setIsVideoPlaying(true);
          } else if (filteredImages?.length > 1) {
            const newIndex = activeIndex > 0 ? activeIndex - 1 : filteredImages.length - 1;
            setActiveIndex(newIndex);
            setIsVideoPlaying(false);
          }
          break;
        case "ArrowRight":
          event.preventDefault();
          if (activeIndex === -1) {
            setActiveIndex(0);
            setIsVideoPlaying(false);
          } else if (activeIndex === filteredImages.length - 1 && product?.video) {
            setActiveIndex(-1);
            setIsVideoPlaying(true);
          } else if (filteredImages?.length > 1) {
            const newIndex = activeIndex < filteredImages.length - 1 ? activeIndex + 1 : 0;
            setActiveIndex(newIndex);
            setIsVideoPlaying(false);
          }
          break;
        case "Escape":
          event.preventDefault();
          setIsImageViewVisible(false);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isImageViewVisible, activeIndex, filteredImages, product?.video]);


  const handleBuyNowClick = async () => {
    if (!userData) {
      setAuthModalVisible(true);
      return;
    }
    if (!selectedColor || selectedColor?._id === "all-color") {
      showErrorToast("Please select a specific color");
      return;
    }
    // Open Buy Now modal
    setBuyNowModalOpen(true);
  };

  const handleReviewSubmit = () => {
    getProductDetails();
  };

  if (loading) {
    return (
      <div className="product-details-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-details-container">
        <HomeHeader fetchDetails={getCartProducts} />
        <Breadcrumb items={breadcrumbItems} />
        <div className="page-content">
          <div className="empty-container">
            <div className="empty-icon">📦</div>
            <p className="empty-text">Product not available at the moment</p>
          </div>
        </div>
        <CommonFooter />
      </div>
    );
  }

  return (
    <div className="product-details-container">
      <HomeHeader fetchDetails={getCartProducts} />
      <Breadcrumb items={breadcrumbItems} />
      <div className="product-details-content">
        {/* Left Column - Image Section */}
        <div className="image-section">
          <div className="image-gallery-container">
            {/* Thumbnail Gallery - Left Side */}
            {(product?.video || filteredImages?.length > 1) && (
              <div className="thumbnail-gallery-left">
                {/* Video Thumbnail - Always at top, non-scrollable */}
                {product?.video && (
                  <div
                    className={`thumbnail-wrapper video-thumbnail ${activeIndex === -1 ? "active" : ""
                      }`}
                    onClick={() => {
                      setActiveIndex(-1);
                      setIsVideoPlaying(true);
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    <img
                      src={preloadedVideoThumbnailUrl || getOptimizedUrl(product?.video?.image)}
                      alt="Video thumbnail"
                      className="thumbnail"
                      loading="lazy"
                    />
                    <div className="thumbnail-overlay"></div>
                    <div className="video-play-button">
                      <FaPlay />
                    </div>
                  </div>
                )}
                {/* Image Thumbnails - Scrollable */}
                {filteredImages?.length > 0 && (
                  <div className="thumbnail-images-container">
                    {filteredImages?.map((img, idx) => (
                      <div
                        key={idx}
                        className={`thumbnail-wrapper ${activeIndex === idx ? "active" : ""
                          }`}
                        onClick={() => {
                          setActiveIndex(idx);
                          setIsVideoPlaying(false);
                        }}
                      >
                        <img
                          src={getPreloadedImageUrl(img?.image)}
                          alt={`Thumbnail ${idx + 1}`}
                          className="thumbnail"
                          loading="lazy"
                        />
                        <div className="thumbnail-overlay"></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Main Image Container */}
            <div className="main-image-container">
              <div className="image-wrapper">
                {activeIndex === -1 && product?.video ? (
                  <video
                    ref={videoRef}
                    src={preloadedVideoUrl || getImageUrl(product?.video?.video)}
                    className="product-video"
                    controls
                    autoPlay={isVideoPlaying}
                    preload="auto"
                    onPlay={() => setIsVideoPlaying(true)}
                    onPause={() => setIsVideoPlaying(false)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <>
                    {/* Image Container - Render all images but only show active one */}
                    {filteredImages?.map((img, idx) => {
                      const isActive = activeIndex === idx;
                      return (
                        <img
                          key={idx}
                          src={getPreloadedImageUrl(img?.image, 0.6)}
                          alt={`Product ${idx + 1}`}
                          className={`product-image ${isActive ? 'active' : 'hidden'}`}
                          onClick={() => {
                            if (isActive) {
                              setIsImageViewVisible(true);
                            }
                          }}
                          onMouseMove={isActive ? handleMouseMove : undefined}
                          onMouseEnter={isActive ? handleMouseEnter : undefined}
                          onMouseLeave={isActive ? handleMouseLeave : undefined}
                          ref={isActive ? imageRef : null}
                          loading="lazy"
                        />
                      );
                    })}

                    {/* Zoom Overlay */}
                    {/* {isZoomed && (
                      <div className="zoom-overlay">
                        <div 
                          className="magnifier"
                          style={{
                            left: `${zoomPosition.x}%`,
                            top: `${zoomPosition.y}%`,
                            transform: 'translate(-50%, -50%)',
                            backgroundImage: `url(${getImageUrl(filteredImages[activeIndex]?.image)})`,
                            backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                            backgroundSize: '200% 200%',
                          }}
                        />
                      </div>
                    )} */}
                  </>
                )}
              </div>

              {/* Separate Zoom View */}
              {/* {isZoomed && preloadZoomView && (
                <div className="separate-zoom-view">
                  <div 
                    className="zoom-view-content"
                    style={{
                      backgroundImage: `url(${preloadZoomView})`,
                      backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                      backgroundSize: '400% 400%',
                    }}
                  />
                </div>
              )} */}

              {/* Navigation Arrows - Only show when there are multiple items to navigate (video + images, or multiple images) */}
              {((product?.video && filteredImages?.length > 0) || filteredImages?.length > 1) && (
                <>
                  <button
                    className="nav-arrow nav-arrow-left"
                    onClick={() => {
                      if (activeIndex === -1) {
                        // From video, go to last image
                        setActiveIndex(filteredImages.length - 1);
                        setIsVideoPlaying(false);
                      } else if (activeIndex === 0 && product?.video) {
                        // From first image, go to video
                        setActiveIndex(-1);
                        setIsVideoPlaying(true);
                      } else {
                        // Normal navigation between images
                        const newIndex = activeIndex > 0 ? activeIndex - 1 : filteredImages.length - 1;
                        setActiveIndex(newIndex);
                        setIsVideoPlaying(false);
                      }
                    }}
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    className="nav-arrow nav-arrow-right"
                    onClick={() => {
                      if (activeIndex === -1) {
                        // From video, go to first image
                        setActiveIndex(0);
                        setIsVideoPlaying(false);
                      } else if (activeIndex === filteredImages.length - 1 && product?.video) {
                        // From last image, go to video
                        setActiveIndex(-1);
                        setIsVideoPlaying(true);
                      } else {
                        // Normal navigation between images
                        const newIndex = activeIndex < filteredImages.length - 1 ? activeIndex + 1 : 0;
                        setActiveIndex(newIndex);
                        setIsVideoPlaying(false);
                      }
                    }}
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}

              {/* Zoom Button - Only show for images, not video */}
              {activeIndex !== -1 && (
                <button
                  className="zoom-button"
                  onClick={() => setIsImageViewVisible(true)}
                >
                  <FaExpand />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Product Information */}
        <div className="product-info-section">
          <div className="product-info">
            <h1 className="detail-product-name">{product?.name}</h1>
            {parseFloat(product?.average_rating) > 0 && (
              <div className="product-average-rating">
                <div className="avg-rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const rating = parseFloat(product?.average_rating);
                    const fillPercentage = Math.max(0, Math.min(100, (rating - (star - 1)) * 100));

                    return (
                      <span
                        key={star}
                        className={`star ${fillPercentage >= 100 ? "filled" : fillPercentage > 0 ? "partial" : ""}`}
                        style={fillPercentage > 0 && fillPercentage < 100 ? { '--fill-percentage': `${fillPercentage}%` } : {}}
                      >
                        ★
                      </span>
                    );
                  })}
                </div>
                <span className="rating-number">{product?.average_rating}</span>
              </div>
            )}
            {productPrice && (
              <div className="product-price-container">
                {selectedSize?.mrp && parseInt(selectedSize?.mrp) > 0 && (
                  <span className="product-price-mrp">
                    {RUPEE}
                    {selectedSize?.mrp}
                  </span>
                )}
                <span className="product-price">
                  {RUPEE}
                  {productPrice}
                </span>
                {product?.on_sale == 1 && (
                  <span className="product-sale-badge">Sale</span>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              className="action-button wishlist-button"
              onClick={onPressWishlist}
            >
              {isWishlisted ? (
                <FaHeart className="heart-icon active" color={colors.red} />
              ) : (
                <FaRegHeart className="heart-icon" />
              )}
            </button>
            <button
              className="action-button wishlist-button"
              onClick={() => setIsShareModalOpen(true)}
            >
              <LuSend className="heart-icon" />
            </button>
            {/* Add to Cart Button */}
            {hasInStockSize && (
              <>
                <div className="cart-button-container">
                  {isProductInCart ? (
                    <CommonButton
                      title="Go to Cart"
                      onPress={openCartDrawer}
                      customClassName="cart-button cart-button-primary"
                    />
                  ) : (
                    <CommonButton
                      title={cartLoading ? "Adding..." : "Add to Cart"}
                      onPress={handleAddToCart}
                      customClassName="cart-button cart-button-primary"
                      disabled={cartLoading}
                    />
                  )}
                </div>
                <div className="cart-button-container">
                  <CommonButton
                    title={"Buy Now"}
                    onPress={handleBuyNowClick}
                    customClassName="cart-button cart-button-primary"
                    disabled={cartLoading}
                  />
                </div>
              </>
            )}
            {/* <button
              className="action-button download-button"
              onClick={handleDownloadImagesSingle}
            >
              <FaDownload className="download-icon" />
            </button>
            <button
              className="action-button zip-button"
              onClick={handleDownloadImagesAsZip}
            >
              <FaFileArchive className="zip-icon" />
            </button> */}
          </div>

          {/* Color Selection */}
          {displayColors?.length > 0 && (
            <div className="colors-section">
              <h3 className="section-title">
                Color:{" "}
                {selectedColor?._id === "all-color"
                  ? "ALL"
                  : selectedColor?.name === "No Color"
                    ? "NO"
                    : selectedColor?.name?.toUpperCase()}
              </h3>
              <div className="color-container">
                {displayColors?.map((color, key) => (
                  <button
                    key={key}
                    className={`color-box-container ${selectedColor?._id === color?._id ? "active" : ""
                      }`}
                    onClick={() => handleColorSelection(color)}
                  >
                    <div
                      className={`color-box ${color?._id === "all-color"
                        ? "all-color-gradient"
                        : color?.name === "No Color" &&
                          color?.hex_code === "rgba(0, 0, 0, 0)"
                          ? "no-color-red"
                          : ""
                        }`}
                      style={{
                        backgroundColor:
                          color?._id === "all-color"
                            ? "transparent"
                            : color?.name === "No Color" &&
                              color?.hex_code === "rgba(0, 0, 0, 0)"
                              ? "transparent"
                              : color?.hex_code,
                      }}
                    />
                    <span className="color-text">
                      {color?.name === "No Color" ? "No" : color?.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size Chart Section */}
          {product?.size_chart?.length > 0 && (
            <div className="size-chart-section">
              <button
                className="size-chart-button"
                onClick={() => setIsSizeChartExpanded(!isSizeChartExpanded)}
              >
                Size Chart
                <FaChevronLeft className={`size-chart-chevron ${isSizeChartExpanded ? 'expanded' : ''}`} />
              </button>
              {isSizeChartExpanded && (
                <div className="size-chart-table-container">
                  <p className="size-chart-description">
                    Please refer to the size chart below to find your perfect fit.
                  </p>
                  <div className="size-chart-table-wrapper">
                    <table className="size-chart-table">
                      <thead>
                        <tr>
                          {product?.size_chart?.map((column, index) => (
                            <th key={index}>{column?.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {product?.size_chart?.[0]?.rows?.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {product?.size_chart?.map((column, colIndex) => {
                              const rowData = column?.rows?.find(r => r?.row_index === rowIndex.toString());
                              return (
                                <td key={colIndex}>{rowData?.name || ''}</td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Size Selection */}
          {product?.sizes?.length > 0 && (
            <div className="size-section">
              <h3 className="section-title">Size: {selectedSize?.size}</h3>
              <div className="size-container">
                {product?.sizes?.map((size, key) => (
                  <button
                    key={key}
                    className={`size-box ${selectedSize?._id === size?._id ? "active" : ""
                      } ${!isProductInStock(size?.unit) ? "out-of-stock" : ""}`}
                    onClick={() => setSelectedSize(size)}
                    disabled={!isProductInStock(size?.unit)}
                  >
                    <span
                      className={`size-text ${selectedSize?._id === size?._id ? "active" : ""
                        }`}
                    >
                      {size?.size}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Product Details */}
          {(product?.fabric || product?.category || product?.brand) && (
            <div className="product-details-section">
              <h3 className="section-title">Product Details</h3>
              {product?.fabric && (
                <div className="detail-container">
                  <span className="detail-title">Fabric:</span>
                  <span className="detail-text">{product?.fabric}</span>
                </div>
              )}
              {product?.category && (
                <div className="detail-container">
                  <span className="detail-title">Category:</span>
                  <span className="detail-text">{product?.category?.name}</span>
                </div>
              )}
              {product?.brand && (
                <div className="detail-container">
                  <span className="detail-title">Brand:</span>
                  <span className="detail-text">{product?.brand?.name}</span>
                </div>
              )}
            </div>
          )}

          {activeCoupon && (
            <div className="product-coupon-section">
              <div className="product-coupon-card">
                <button
                  type="button"
                  className="product-coupon-nav product-coupon-nav-left"
                  onClick={() => setActiveCouponIndex((prev) => prev - 1)}
                  disabled={activeCouponIndex === 0}
                  aria-label="Previous coupon"
                >
                  <FaChevronLeft />
                </button>

                <div className="product-coupon-content">
                  <p className="product-coupon-title">
                    {getCouponTitle(activeCoupon)}
                  </p>
                  <button
                    type="button"
                    className="product-coupon-code-box"
                    onClick={() => handleCopyCouponCode(activeCoupon)}
                  >
                    <span className="product-coupon-code-text">
                      {copiedCouponId === activeCoupon?._id
                        ? "Copied"
                        : activeCoupon?.code}
                    </span>
                    <LuCopy className="product-coupon-copy-icon" />
                  </button>
                </div>

                <button
                  type="button"
                  className="product-coupon-nav product-coupon-nav-right"
                  onClick={() => setActiveCouponIndex((prev) => prev + 1)}
                  disabled={activeCouponIndex === coupons.length - 1}
                  aria-label="Next coupon"
                >
                  <FaChevronRight />
                </button>
                {coupons.length > 1 && (
                  <div className="product-coupon-dots">
                    {coupons.map((coupon, index) => (
                      <button
                        type="button"
                        key={coupon?._id || index}
                        className={`product-coupon-dot ${index === activeCouponIndex ? "active" : ""
                          }`}
                        onClick={() => setActiveCouponIndex(index)}
                        aria-label={`Go to coupon ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dynamic Tab Interface - Full Width */}
      {(product?.description || product?.return_policy) && (
        <div className="tab-full-section">
          <div className="tab-full-content">
            <div className="single-tab-container">
              <div className="tab-group">
                {product?.description && (
                  <div
                    className={`single-tab ${descriptionExpanded ? "active" : "inactive"
                      }`}
                    onClick={() => {
                      setDescriptionExpanded(true);
                      setReturnPolicyExpanded(false);
                    }}
                  >
                    <span className="tab-text">Description</span>
                  </div>
                )}
                {product?.return_policy && (
                  <div
                    className={`single-tab ${returnPolicyExpanded ? "active" : "inactive"
                      }`}
                    onClick={() => {
                      setReturnPolicyExpanded(true);
                      setDescriptionExpanded(false);
                    }}
                  >
                    <span className="tab-text">Return Policy</span>
                  </div>
                )}
              </div>
            </div>

            <div className="tab-content">
              {descriptionExpanded && product?.description && (
                <div className="tab-panel">
                  <div
                    dangerouslySetInnerHTML={{ __html: product?.description }}
                  />
                </div>
              )}

              {returnPolicyExpanded && product?.return_policy && (
                <div className="tab-panel">
                  <div
                    dangerouslySetInnerHTML={{ __html: product?.return_policy }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* You May Also Like Section */}
      {relatedProducts?.length > 0 && (
        <div className="you-may-also-like-section">
          <div className="you-may-also-like-content">
            <h2 className="you-may-also-like-title">You May Also Like</h2>
            <div className="you-may-also-like-scroll">
              {relatedProducts?.map((item) => (
                <div key={item?._id} className="you-may-also-like-item">
                  <ProductCard item={item} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rating Section */}
      <RatingSection product={product} onReviewSubmit={handleReviewSubmit} />

      {/* Reviews Section - Full Width */}
      {product?.rating_reviews?.length > 0 && (
        <div className="reviews-full-section">
          <div className="reviews-full-content">
            <h3 className="reviews-full-title">Rating & Reviews</h3>
            <div className="reviews-list">
              {product?.rating_reviews?.map((review, key) => (
                <div key={key} className="review-item">
                  {review?.user?.profile_image ? (
                    <img
                      src={getImageUrl(review?.user?.profile_image)}
                      alt={review?.user?.name}
                      className="review-avatar-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="avatar-circle">
                      <span className="avatar-text">
                        {getInitials(review?.user?.name)}
                      </span>
                    </div>
                  )}
                  <div className="user-details">
                    <h4 className="review-user-name">{review?.user?.name}</h4>
                    {review?.rating && review?.rating !== 0 && (
                      <div className="detail-rating-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`star ${star <= review?.rating ? "filled" : ""
                              }`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    )}
                    {review?.review && (
                      <p className="review-text">{review?.review}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <ImageViewer
        images={filteredImages?.map((img) => getImageUrl(img?.image)) || []}
        currentIndex={activeIndex >= 0 ? activeIndex : 0}
        isVisible={isImageViewVisible && activeIndex !== -1}
        onClose={() => setIsImageViewVisible(false)}
        onNext={() => {
          const newIndex =
            activeIndex < filteredImages.length - 1 ? activeIndex + 1 : 0;
          setActiveIndex(newIndex);
          setIsVideoPlaying(false);
        }}
        onPrevious={() => {
          const newIndex =
            activeIndex > 0 ? activeIndex - 1 : filteredImages.length - 1;
          setActiveIndex(newIndex);
          setIsVideoPlaying(false);
        }}
      />

      {downloading && (
        <div className="downloading-overlay">
          <div className="downloading-content">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${downloadProgress}%` }}
              />
            </div>
            <p>Downloading... {downloadProgress}%</p>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareProduct
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        product={product}
        productId={product_id}
      />

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartDrawerOpen} onClose={closeCartDrawer} />

      {/* Auth Modal */}
      <AuthModal
        visible={isAuthModalVisible}
        onClose={() => setAuthModalVisible(false)}
      />

      {/* Buy Now Modal */}
      <BuyNowModal
        isOpen={isBuyNowModalOpen}
        onClose={() => setBuyNowModalOpen(false)}
        product={product}
        selectedColor={selectedColor}
        selectedSize={selectedSize}
      />

      <CommonFooter
        productInfo={{
          name: product?.name || "Product",
          url: `${window.location.origin}${window.location.pathname}?product_id=${product_id}`,
        }}
      />
    </div>
  );
};

export default ProductDetails;
